
import { Component } from 'react';

import logger from '../logger';
import { safePlayMediaElement } from '../playbackUtils';

/**
 * Describes audio element interface used in the base/media feature for audio
 * playback.
 */
export type AudioElement = {
    currentTime: number,
    pause: () => void,
    play: () => void,
    setSinkId?: string => Function,
    stop: () => void
};

/**
 * {@code AbstractAudio} Component's property types.
 */
type Props = {

    /**
     * A callback which will be called with {@code AbstractAudio} instance once
     * the audio element is loaded.
     */
    setRef?: ?AudioElement => void,

    /**
     * The URL of a media resource to use in the element.
     *
     * NOTE on react-native sound files are imported through 'require' and then
     * passed as the 'src' parameter which means their type will be 'any'.
     *
     * @type {Object | string}
     */
    src: Object | string,
    stream?: Object,
    loop?: ?boolean
}

/**
 * The React {@link Component} which is similar to Web's
 * {@code HTMLAudioElement}.
 */
export default class AbstractAudio extends Component<Props> {
    /**
     * The {@link AudioElement} instance which implements the audio playback
     * functionality.
     */
    _audioElementImpl: ?AudioElement;

    /**
     * Initializes a new {@code AbstractAudio} instance.
     *
     * @param {Props} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        // Bind event handlers so they are only bound once per instance.
        this.setAudioElementImpl = this.setAudioElementImpl.bind(this);
    }

    /**
     * Attempts to pause the playback of the media.
     *
     * @public
     * @returns {void}
     */
    pause(): void {
        this._audioElementImpl && this._audioElementImpl.pause();
    }

    /**
     * Attempts to begin the playback of the media.
     *
     * @public
     * @returns {void}
     */
    play(): void {
        this._play(/* allowReload */ true);
    }

    /**
     * Plays the element, reloading its source at most once when the browser reports it unplayable.
     *
     * @param {boolean} allowReload - Whether a NotSupportedError may trigger a reload and a second attempt.
     * @private
     * @returns {void}
     */
    _play(allowReload: boolean): void {
        const element = this._audioElementImpl;

        if (!element) {
            return;
        }

        safePlayMediaElement(element, {
            onAutoplayBlocked: error => {
                logger.warn('Audio play blocked by browser policy:', error);
            },
            onError: error => {
                // WebKit rejects play() with NotSupportedError for as long as the element sits in
                // MEDIA_ERR_SRC_NOT_SUPPORTED, a state a loaded element falls into when a reload of its source fails.
                // Only load() leaves that state, so without it every later sound on this element failed too.
                if (allowReload && error?.name === 'NotSupportedError' && !this._reloadingSource
                        && typeof element.load === 'function' && typeof element.addEventListener === 'function') {
                    this._reloadAndPlay(element, error);

                    return;
                }

                logger.error('Audio play failed:', error);
            }
        });
    }

    _reloadingSource: boolean;

    /**
     * Reloads the element's source and plays it once it can.
     *
     * @param {HTMLMediaElement} element - The media element.
     * @param {Error} playError - The play() rejection that prompted the reload.
     * @private
     * @returns {void}
     */
    _reloadAndPlay(element: Object, playError: Error): void {
        const handlers = {};
        const cleanup = () => {
            element.removeEventListener('canplay', handlers.canplay);
            element.removeEventListener('error', handlers.error);
            this._reloadingSource = false;
        };

        handlers.canplay = () => {
            cleanup();
            this._play(/* allowReload */ false);
        };
        handlers.error = () => {
            cleanup();
            logger.error('Audio play failed:', playError, element.error);
        };

        this._reloadingSource = true;
        element.addEventListener('canplay', handlers.canplay);
        element.addEventListener('error', handlers.error);
        element.load();
    }

    setAudioElementImpl: ?AudioElement => void;

    /**
     * Set the (reference to the) {@link AudioElement} object which implements
     * the audio playback functionality.
     *
     * @param {AudioElement} element - The {@link AudioElement} instance
     * which implements the audio playback functionality.
     * @protected
     * @returns {void}
     */
    setAudioElementImpl(element: ?AudioElement): void {
        this._audioElementImpl = element;

        // setRef
        const { setRef } = this.props;

        // $FlowFixMe
        typeof setRef === 'function' && setRef(element ? this : null);
    }

    /**
     * Sets the sink ID (output device ID) on the underlying audio element.
     * NOTE: Currently, implemented only on Web.
     *
     * @param {string} sinkId - The sink ID (output device ID).
     * @returns {void}
     */
    setSinkId(sinkId: string): void {
        if (!this._audioElementImpl || typeof this._audioElementImpl.setSinkId !== 'function') {
            return;
        }

        this._audioElementImpl.setSinkId(sinkId)
            .catch(error => {
                const errorMessage = error?.message || '';

                // Handle NotFoundError gracefully - device may have been unplugged
                if (error.name === 'NotFoundError' || errorMessage.includes('not found')) {
                    logger.warn(
                        'Audio output device not found, using default device',
                        { sinkId,
                            error }
                    );

                    if (sinkId !== 'default' && this._audioElementImpl) {
                        this._audioElementImpl.setSinkId('default').catch(() => {
                            // Silently ignore fallback errors
                        });
                    }
                } else if (error.name === 'NotAllowedError'
                    || /not allowed|user gesture|permission/i.test(errorMessage)) {
                    logger.warn(
                        'Audio output switch requires user interaction, keeping current output device',
                        { sinkId,
                            error }
                    );
                } else if (error.name === 'AbortError') {
                    // setSinkId commonly rejects with AbortError when the
                    // element is in a transient state (no source attached,
                    // paused, or a new setSinkId call superseded this one).
                    // Treat as a warning - the next attempt typically succeeds.
                    logger.warn(
                        'Audio output switch was aborted, will retry on next setSinkId',
                        { sinkId,
                            error }
                    );
                } else {
                    logger.error('Error setting sink', error);
                }
            });
    }

    /**
     * Attempts to stop the playback of the media.
     *
     * @public
     * @returns {void}
     */
    stop(): void {
        this._audioElementImpl && this._audioElementImpl.stop();
    }
}
